import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, animate } from "framer-motion";
import { Logo } from "@/components/Logo";
import { SECURITY_POINTS } from "@/pages/Security";
import { openCookieSettings } from "@/components/CookieConsent";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Users, Calendar, FileText, Mic, Sparkles, ShieldCheck,
  GitBranch, Check, RefreshCw, Briefcase, Crown, Zap, BarChart3,
  Workflow, Plus, TrendingUp, Bell, Mail, Menu, X,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

// Count-up number that animates when scrolled into view
const CountUp = ({ to, suffix = "", decimals = 0, className }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.4, ease: "easeOut",
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref} className={className}>{val.toFixed(decimals)}{suffix}</span>;
};

// Restrained accent palette (muted jewel tones) for tasteful, meaningful color
const ACCENTS = [
  { bg: "bg-[#EEF5FB]", text: "text-[#2D74B4]", solid: "#2D74B4" }, // info / slate blue
  { bg: "bg-[#EDF8F2]", text: "text-[#1F8B53]", solid: "#28A263" }, // success / emerald
  { bg: "bg-[#FBF4E2]", text: "text-[#A97A00]", solid: "#D99A00" }, // warning / ochre
  { bg: "bg-[#FBEFF1]", text: "text-[#C13348]", solid: "#D94255" }, // danger / coral
  { bg: "bg-[#F4ECF8]", text: "text-[#7E4490]", solid: "#8A4C9E" }, // plum
];

// Section label pill (icon chip + uppercase label) with optional soft tint
const Label = ({ icon: Icon, children, dark, tint }) => (
  <div className="flex items-center gap-2 mb-4">
    <span className={`h-7 w-7 rounded-lg flex items-center justify-center ${dark ? "bg-white/10 text-white" : tint ? `${tint.bg} ${tint.text}` : "bg-[#516670] text-white"}`}><Icon className="h-3.5 w-3.5" /></span>
    <span className={`text-xs font-semibold uppercase tracking-[0.18em] ${dark ? "text-neutral-300" : tint ? tint.text : "text-neutral-500"}`}>{children}</span>
  </div>
);

// Animated circular score ring (echoes the reference design)
const RingStat = ({ value, max, color, label, delay }) => {
  const C = 2 * Math.PI * 16;
  const offset = C * (1 - value / max);
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative h-12 w-12">
        <svg viewBox="0 0 40 40" className="h-12 w-12 -rotate-90">
          <circle cx="20" cy="20" r="16" fill="none" stroke="#f0f0f0" strokeWidth="3.5" />
          <motion.circle cx="20" cy="20" r="16" fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round"
            strokeDasharray={C} initial={{ strokeDashoffset: C }} animate={{ strokeDashoffset: offset }} transition={{ delay, duration: 1.1, ease: "easeOut" }} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-heading text-xs font-semibold text-neutral-900">{value}</span>
      </div>
      <span className="text-[9px] text-neutral-400">{label}</span>
    </div>
  );
};

const features = [
  { icon: Users, title: "Every principal, fully mapped", desc: "Keep each principal's people, staff, advisors and contacts structured and instantly searchable." },
  { icon: GitBranch, title: "Relationship intelligence", desc: "See how people, contacts and obligations connect so nothing slips through." },
  { icon: FileText, title: "Documents & expiries", desc: "Passports, visas, insurance and contracts with automatic expiry visibility." },
  { icon: RefreshCw, title: "Never miss a renewal", desc: "One timeline for everything that lapses, across every principal." },
  { icon: Calendar, title: "One shared calendar", desc: "Travel, meetings and appointments coordinated across your whole team." },
  { icon: Mic, title: "Notes → tasks, instantly", desc: "Capture by voice or text; Swishr drafts tasks you review before they hit the record." },
];

const steps = [
  { icon: Users, t: "Connect your principal", d: "Add a principal and link their people, contacts, staff and advisors into one network." },
  { icon: Workflow, t: "Centralise the records", d: "Documents, renewals and the shared calendar — your single source of truth." },
  { icon: BarChart3, t: "Run the day with AI", d: "Start from the daily brief, capture notes, and let Swishr draft tasks for review." },
];

const roles = [
  { icon: Briefcase, name: "PAs & EAs" },
  { icon: Crown, name: "Chiefs of Staff" },
  { icon: Users, name: "Family offices" },
  { icon: ShieldCheck, name: "Principals (viewer)" },
];

const highlights = [
  { title: "One hub", label: "People, documents, renewals, calendar & tasks — together" },
  { title: "AI daily brief", label: "Drafts your day and turns notes into tasks for review" },
  { title: "Multi-principal", label: "Manage several principals, each with their own world" },
];

const pricing = {
  monthly: [
    { name: "Solo", price: "£39", icon: Zap, tagline: "For an individual PA or EA", features: ["1 principal", "Directory, calendar & tasks", "Documents & renewals", "Voice & typed notes", "Email support"] },
    { name: "Team", price: "£129", icon: Users, featured: true, tagline: "For support teams", features: ["Up to 5 seats", "Multiple principals", "AI daily brief & alerts", "Roles & permissions", "Priority support"] },
    { name: "Family Office", price: "Custom", icon: Crown, tagline: "For larger operations", features: ["Unlimited seats & principals", "Platform admin & oversight", "SSO & advanced security", "Audit-ready records", "Dedicated manager"] },
  ],
  yearly: [
    { name: "Solo", price: "£33", icon: Zap, tagline: "For an individual PA or EA", features: ["1 principal", "Directory, calendar & tasks", "Documents & renewals", "Voice & typed notes", "Email support"] },
    { name: "Team", price: "£109", icon: Users, featured: true, tagline: "For support teams", features: ["Up to 5 seats", "Multiple principals", "AI daily brief & alerts", "Roles & permissions", "Priority support"] },
    { name: "Family Office", price: "Custom", icon: Crown, tagline: "For larger operations", features: ["Unlimited seats & principals", "Platform admin & oversight", "SSO & advanced security", "Audit-ready records", "Dedicated manager"] },
  ],
};

const faqs = [
  { q: "Who is Swishr Central built for?", a: "Personal assistants, executive assistants, chiefs of staff and family office teams who manage one or more principals — their schedules, people, documents, renewals and day-to-day operations." },
  { q: "Do you offer a free trial?", a: "Yes — every plan starts with a free trial so your team can experience the full workflow before committing." },
  { q: "What does the AI actually do?", a: "It drafts your daily brief and turns voice or typed notes into suggested tasks. Nothing is added to your records until you review and confirm it." },
  { q: "Can I manage more than one principal?", a: "Yes — Team and Family Office plans support multiple principals, each with their own people, documents and timelines." },
  { q: "How is my data kept separate from other customers?", a: "Swishr Central is multi-tenant by design. Every request is scoped to your own workspace using your signed, authenticated session, so your records stay isolated from every other customer — you only ever see your own workspace's data." },
  { q: "How are my passwords and login protected?", a: "Passwords are salted and hashed with bcrypt and never stored in plain text — not even we can read them. Sessions use signed tokens in secure, HTTP-only cookies, and all traffic is encrypted in transit with TLS/HTTPS." },
  { q: "Who at Swishr can see my data?", a: "Access is role-based and kept to a minimum. If our team ever needs to look into a workspace to support you, that 'view-as' mode is strictly read-only — no changes can be made — and the action is recorded." },
  { q: "Do you use my data to train AI, or sell it?", a: "No. We do not sell your data, and we do not permit our AI providers to train their general models on your content where that control is available to us. AI only processes what's needed to return a result to you, and its suggestions stay separate from your records until you confirm them." },
  { q: "Are you GDPR compliant and UK-based?", a: "Swishr Central is operated by Swishr Digital Ltd, a company registered in England and Wales (company no. 17391541), and is built to align with the UK GDPR and the Data Protection Act 2018. See our Privacy Policy for full detail." },
  { q: "Can I export or delete my data?", a: "Yes. You can ask us to provide a copy of your workspace data or to permanently delete it at any time. You also have the right to complain to the UK Information Commissioner's Office (ICO) if you're ever unhappy with how we handle your data." },
  { q: "How do I report a security concern?", a: "If you believe you've found a security issue, contact us in writing at our registered office — Swishr Digital Ltd, Flat 6 Logan House, 1 Logan Place, London, W8 6QN — and we'll investigate. You can read our full approach on the Security page." },
];

// Audience-led hero visual: a professional assistant with floating productivity cards
const PORTRAIT_URL = "https://static.prod-images.emergentagent.com/jobs/e5d4b071-aa3f-46c9-b75b-fecf11f4132f/images/9924a50b40488c2179722f9cd0a10571fd1fb51d47a539e59f4092753ebace2e.png";

const HeroPortrait = () => (
  <div className="relative mx-auto w-[320px] sm:w-[400px] lg:w-[460px] aspect-square">
    {/* soft accent glow */}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[86%] w-[86%] rounded-full bg-[#2D74B4]/10 blur-3xl" />
    {/* dashed accent orbit ring */}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[94%] w-[94%] rounded-full border border-dashed border-neutral-300/70" />
    {/* circular portrait */}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[86%] w-[86%] rounded-full overflow-hidden ring-1 ring-neutral-900/10 shadow-2xl shadow-neutral-900/15 z-10">
      <img src={PORTRAIT_URL} alt="Executive assistant managing a principal's world with Swishr Central" className="h-full w-full object-cover" />
    </div>

    {/* Floating: AI alert (top-left) */}
    <motion.div initial={{ opacity: 0, x: -20, y: -6 }} animate={{ opacity: 1, x: 0, y: 0 }} transition={{ delay: 0.7, duration: 0.5 }}
      className="absolute left-0 sm:-left-4 top-10 z-20 animate-float-soft">
      <div className="flex items-center gap-2.5 rounded-2xl bg-[#516670] text-white px-3.5 py-2.5 shadow-2xl shadow-neutral-900/30 border-l-2 border-[#D94255]">
        <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#D94255]/70" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#D94255]" /></span>
        <div className="leading-tight"><p className="text-[9px] uppercase tracking-wider text-neutral-400">AI alert</p><p className="text-xs font-semibold">Visa expires in 14 days</p></div>
      </div>
    </motion.div>

    {/* Floating: app-icon tile (top-right) */}
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.9, duration: 0.5 }}
      className="absolute right-0 sm:-right-3 top-20 z-20 animate-float-soft" style={{ animationDelay: "1.2s" }}>
      <div className="grid grid-cols-3 gap-1.5 rounded-2xl bg-white border border-neutral-200 p-2.5 shadow-2xl shadow-neutral-900/10">
        {[{ I: Mail, a: 0 }, { I: Calendar, a: 1 }, { I: FileText, a: 2 }, { I: Users, a: 3 }, { I: Bell, a: 4 }, { I: Check, a: 1 }].map(({ I, a }, k) => (
          <span key={k} className={`h-8 w-8 rounded-lg flex items-center justify-center ${ACCENTS[a].bg} ${ACCENTS[a].text}`}><I className="h-4 w-4" /></span>
        ))}
      </div>
    </motion.div>

    {/* Floating: rings mini-card (bottom-left) */}
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.5 }}
      className="absolute left-0 sm:-left-6 bottom-8 z-20 animate-float-soft" style={{ animationDelay: "0.6s" }}>
      <div className="rounded-2xl bg-white border border-neutral-200 px-3.5 py-2.5 shadow-2xl shadow-neutral-900/10 flex gap-4">
        <RingStat value={9} max={12} color="#2D74B4" label="People" delay={1.2} />
        <RingStat value={3} max={6} color="#D99A00" label="Renewals" delay={1.35} />
      </div>
    </motion.div>

    {/* Floating: task drafted pill (bottom-right) */}
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.5 }}
      className="absolute right-0 sm:-right-2 bottom-20 z-20 animate-float-soft" style={{ animationDelay: "1.8s" }}>
      <div className="flex items-center gap-2.5 rounded-2xl bg-white border border-neutral-200 px-3.5 py-2.5 shadow-2xl shadow-neutral-900/10">
        <span className="h-8 w-8 rounded-lg bg-[#28A263] text-white flex items-center justify-center"><Check className="h-4 w-4" /></span>
        <div className="leading-tight"><p className="text-xs font-semibold text-neutral-900">Task drafted</p><p className="text-[10px] text-neutral-400">from voice note</p></div>
      </div>
    </motion.div>
  </div>
);

export default function Landing() {
  const [yearly, setYearly] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const plans = yearly ? pricing.yearly : pricing.monthly;

  return (
    <div className="min-h-screen bg-white text-neutral-900 font-sans overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-neutral-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-32 flex items-center justify-between">
          <Link to="/" data-testid="logo-home-link"><Logo className="h-20 sm:h-28" /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-neutral-600">
            <a href="#how" className="hover:text-neutral-900 transition-colors">How it works</a>
            <a href="#features" className="hover:text-neutral-900 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-neutral-900 transition-colors">Pricing</a>
            <Link to="/about" className="hover:text-neutral-900 transition-colors">About</Link>
            <a href="#security" className="hover:text-neutral-900 transition-colors">Security</a>
            <a href="#faq" className="hover:text-neutral-900 transition-colors">FAQ</a>
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" data-testid="nav-login" className="text-neutral-700 rounded-full">Log in</Button></Link>
            <Link to="/register"><Button data-testid="nav-cta" className="rounded-full bg-[#516670] hover:bg-[#41545d] gap-1.5">Get started <ArrowRight className="h-4 w-4" /></Button></Link>
          </div>
          <button onClick={() => setMobileOpen((o) => !o)} data-testid="mobile-menu-toggle"
            className="md:hidden h-10 w-10 rounded-lg flex items-center justify-center text-neutral-700 hover:bg-neutral-100 transition-colors" aria-label="Toggle menu">
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-neutral-100 bg-white" data-testid="mobile-menu">
            <nav className="px-4 py-4 flex flex-col gap-1 text-sm font-medium text-neutral-700">
              <a href="#how" onClick={() => setMobileOpen(false)} className="py-2.5 px-2 rounded-lg hover:bg-neutral-50">How it works</a>
              <a href="#features" onClick={() => setMobileOpen(false)} className="py-2.5 px-2 rounded-lg hover:bg-neutral-50">Features</a>
              <a href="#pricing" onClick={() => setMobileOpen(false)} className="py-2.5 px-2 rounded-lg hover:bg-neutral-50">Pricing</a>
              <Link to="/about" onClick={() => setMobileOpen(false)} className="py-2.5 px-2 rounded-lg hover:bg-neutral-50">About</Link>
              <a href="#faq" onClick={() => setMobileOpen(false)} className="py-2.5 px-2 rounded-lg hover:bg-neutral-50">FAQ</a>
              <div className="flex flex-col gap-2 pt-3 mt-2 border-t border-neutral-100">
                <Link to="/login" onClick={() => setMobileOpen(false)}><Button variant="outline" className="w-full rounded-full border-neutral-300" data-testid="mobile-nav-login">Log in</Button></Link>
                <Link to="/register" onClick={() => setMobileOpen(false)}><Button className="w-full rounded-full bg-[#516670] hover:bg-[#41545d] gap-1.5" data-testid="mobile-nav-cta">Get started <ArrowRight className="h-4 w-4" /></Button></Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-fade [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_70%)]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[480px] w-[900px] bg-neutral-300/30 blur-[120px] rounded-full -z-0" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 lg:pt-16 pb-12">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-6 items-center">
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3.5 py-1.5 text-xs font-medium text-neutral-600 shadow-sm">
                <ShieldCheck className="h-3.5 w-3.5 text-[#516670]" />
                Built for PAs, EAs &amp; family-office teams
              </div>
              <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                Total operational control for <span className="text-shimmer">elite support teams</span>.
              </h1>
              <p className="mt-6 text-lg text-neutral-500 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Swishr Central is the command centre for PAs, EAs and family office teams — manage every principal's people, documents, renewals, calendar and tasks in one trusted place.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link to="/register"><Button size="lg" data-testid="hero-cta" className="rounded-full bg-[#516670] hover:bg-[#41545d] gap-2 w-full sm:w-auto px-7 transition-transform hover:scale-[1.03]">Get started <ArrowRight className="h-4 w-4" /></Button></Link>
                <a href="#features"><Button size="lg" variant="outline" className="rounded-full w-full sm:w-auto border-neutral-300 px-7 hover:bg-neutral-50">See features</Button></a>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }} className="order-first lg:order-last">
              <HeroPortrait />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Positioning band */}
      <section className="py-10 border-y border-neutral-100 bg-neutral-50/50">
        <p className="text-center text-sm text-neutral-500 max-w-2xl mx-auto px-4 leading-relaxed">
          Built for PAs, EAs and family-office teams — one calm command centre for every principal's people, documents, renewals and day-to-day.
        </p>
      </section>

      {/* Highlights band */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {highlights.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
              <p className="font-heading text-xl sm:text-2xl font-semibold text-neutral-900">{s.title}</p>
              <p className="mt-2 text-sm text-neutral-500 max-w-xs mx-auto leading-relaxed">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="flex justify-center"><Label icon={Workflow} tint={ACCENTS[0]}>Process</Label></div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">How Swishr Central works</h2>
          <p className="mt-4 text-neutral-500">Three simple steps to bring an entire operation under control.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
              className="rounded-2xl border border-neutral-200 bg-white p-7 text-center hover:shadow-xl hover:shadow-neutral-900/5 hover:-translate-y-1 transition-all duration-300">
              <div className={`mx-auto h-12 w-12 rounded-xl ${ACCENTS[i].bg} ${ACCENTS[i].text} flex items-center justify-center mb-4`}><s.icon className="h-5 w-5" /></div>
              <span className="text-xs font-semibold text-neutral-400">Step {i + 1}</span>
              <h3 className="mt-1 font-heading text-lg font-semibold">{s.t}</h3>
              <p className="mt-2 text-sm text-neutral-500 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="flex justify-center"><Label icon={Sparkles} tint={ACCENTS[4]}>Features</Label></div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Everything you manage, in one hub</h2>
          <p className="mt-4 text-neutral-500">Swishr Central eliminates scattered tools — from people and documents to renewals and daily coordination.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
              className="group rounded-2xl border border-neutral-200 bg-white p-6 hover:shadow-xl hover:shadow-neutral-900/5 hover:-translate-y-1.5 transition-all duration-300">
              <div className={`h-11 w-11 rounded-xl ${ACCENTS[i % 5].bg} ${ACCENTS[i % 5].text} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}><f.icon className="h-5 w-5" /></div>
              <h3 className="font-heading text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-neutral-500 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* AI banner */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="rounded-3xl bg-[#516670] text-white px-8 py-12 sm:px-14 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 h-60 w-60 bg-white/10 blur-[90px] rounded-full" />
          <div className="absolute inset-0 bg-grid-fade opacity-[0.15]" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Label icon={Sparkles} dark>AI & voice</Label>
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Bounded AI you can actually trust.</h2>
              <p className="mt-4 text-neutral-300 leading-relaxed">Swishr Central drafts your daily brief and turns notes into suggested tasks — but confirmed records and AI suggestions stay visibly separate. Nothing is saved without your review.</p>
              <ul className="mt-5 space-y-2.5">
                {["AI daily brief of what needs attention", "Voice or typed notes → reviewable tasks", "Suggestions clearly marked"].map((t) => (
                  <li key={t} className="flex items-center gap-2.5 text-neutral-200 text-sm"><span className="h-5 w-5 rounded-full bg-white/10 text-white flex items-center justify-center"><Check className="h-3 w-3" /></span>{t}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5 backdrop-blur">
              <div className="flex items-center gap-2 mb-3"><span className="h-8 w-8 rounded-lg bg-white text-neutral-900 flex items-center justify-center"><Mic className="h-4 w-4" /></span><span className="text-sm font-semibold">Note → tasks</span><span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-neutral-300 bg-white/10 px-2 py-0.5 rounded-full">AI draft</span></div>
              {["Book passport appointment", "Check Geneva calendar clash", "Confirm airport car"].map((t, i) => (
                <motion.div key={t} initial={{ opacity: 0, x: 12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 + i * 0.12 }}
                  className="flex items-center gap-3 bg-white/5 rounded-lg border border-white/10 px-3 py-2.5 mb-2 text-sm text-neutral-200"><span className="h-4 w-4 rounded border border-white/30" />{t}</motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Roles strip */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {roles.map((r, i) => (
            <motion.div key={r.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="rounded-2xl border border-neutral-200 bg-white p-6 flex items-center gap-3 hover:border-neutral-400 transition-colors">
              <span className={`h-10 w-10 rounded-xl ${ACCENTS[i % 5].bg} ${ACCENTS[i % 5].text} flex items-center justify-center`}><r.icon className="h-5 w-5" /></span>
              <span className="font-heading font-semibold text-neutral-800">{r.name}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Security */}
      <section id="security" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="flex justify-center"><Label icon={ShieldCheck} tint={ACCENTS[1]}>Security &amp; privacy</Label></div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Discretion your principals expect</h2>
          <p className="mt-4 text-neutral-500">Family offices trust us with sensitive information. Here is exactly how we protect it — no jargon, no overclaiming.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SECURITY_POINTS.map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
              className="rounded-2xl border border-neutral-200 bg-white p-6 hover:shadow-lg hover:shadow-neutral-900/5 transition-shadow">
              <div className="h-11 w-11 rounded-xl bg-[#EEF2F4] text-[#516670] flex items-center justify-center mb-4"><p.icon className="h-5 w-5" /></div>
              <h3 className="font-heading text-lg font-semibold text-neutral-900">{p.title}</h3>
              <p className="mt-2 text-sm text-neutral-500 leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/security"><Button variant="outline" className="rounded-full border-neutral-300 px-6 gap-2" data-testid="security-overview-link">Read our security overview <ArrowRight className="h-4 w-4" /></Button></Link>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="flex justify-center"><Label icon={Zap} tint={ACCENTS[1]}>Pricing</Label></div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Simple, transparent pricing</h2>
          <p className="mt-4 text-neutral-500">Start solo, grow into a team, or run a full family office.</p>
          <div className="mt-6 inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white p-1" data-testid="pricing-toggle">
            <button onClick={() => setYearly(false)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${!yearly ? "bg-[#516670] text-white" : "text-neutral-500"}`}>Monthly</button>
            <button onClick={() => setYearly(true)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${yearly ? "bg-[#516670] text-white" : "text-neutral-500"}`}>Yearly <span className="text-[10px] bg-neutral-200 text-neutral-700 px-1.5 py-0.5 rounded-full">-15%</span></button>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6 items-start">
          {plans.map((p, i) => (
            <motion.div key={p.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              data-testid={`pricing-${p.name.toLowerCase().replace(/ /g, "-")}`}
              className={`rounded-3xl border p-7 relative transition-transform hover:-translate-y-1 ${p.featured ? "border-neutral-900 bg-[#516670] text-white shadow-2xl shadow-neutral-900/20 md:-mt-3" : "border-neutral-200 bg-white"}`}>
              {p.featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-semibold uppercase tracking-wider bg-white text-neutral-900 px-3 py-1 rounded-full">Most popular</span>}
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-4 ${p.featured ? "bg-white text-neutral-900" : "bg-neutral-100 text-neutral-900"}`}><p.icon className="h-5 w-5" /></div>
              <h3 className="font-heading text-lg font-semibold">{p.name}</h3>
              <p className={`text-sm ${p.featured ? "text-neutral-300" : "text-neutral-400"}`}>{p.tagline}</p>
              <div className="mt-5 flex items-end gap-1">
                <span className="font-heading text-4xl font-semibold">{p.price}</span>
                {p.price !== "Custom" && <span className={`mb-1 text-sm ${p.featured ? "text-neutral-400" : "text-neutral-400"}`}>/{yearly ? "mo, billed yearly" : "month"}</span>}
              </div>
              <Link to={p.price === "Custom" ? "/login" : "/register"} className="block mt-6">
                <Button data-testid={`pricing-cta-${p.name.toLowerCase().replace(/ /g, "-")}`} className={`w-full rounded-full ${p.featured ? "bg-white text-neutral-900 hover:bg-neutral-200" : "bg-[#516670] hover:bg-[#41545d]"}`}>
                  {p.price === "Custom" ? "Contact sales" : "Start free trial"}
                </Button>
              </Link>
              <ul className="mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2.5 text-sm ${p.featured ? "text-neutral-200" : "text-neutral-600"}`}>
                    <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${p.featured ? "text-white" : "text-neutral-900"}`} />{f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <div className="text-center mb-10">
          <div className="flex justify-center"><Label icon={Plus} tint={ACCENTS[3]}>FAQs</Label></div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Your questions, answered</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border border-neutral-200 rounded-2xl px-5 bg-white" data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-medium text-neutral-800 hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-neutral-500 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <div className="rounded-[2rem] bg-[#516670] px-8 py-16 sm:px-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-fade opacity-[0.15]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[300px] w-[600px] bg-white/10 blur-[110px] rounded-full" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-neutral-200 mb-5"><Sparkles className="h-3.5 w-3.5" /> Ready when you are</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight max-w-xl mx-auto">Take control of every principal's world.</h2>
            <p className="mt-4 text-neutral-300 max-w-lg mx-auto">Step inside a fully realised workspace — people, documents, renewals, calendar, AI brief and reports.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/login"><Button size="lg" data-testid="footer-cta" className="rounded-full bg-white text-neutral-900 hover:bg-neutral-200 gap-2 w-full sm:w-auto px-7 transition-transform hover:scale-[1.03]">Enter the demo <ArrowRight className="h-4 w-4" /></Button></Link>
              <Link to="/login"><Button size="lg" variant="outline" className="rounded-full w-full sm:w-auto bg-transparent text-white border-white/20 hover:bg-white/10 px-7">Request a demo</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" data-testid="footer-logo-home-link"><Logo className="h-28 sm:h-40" /></Link>
          <p className="text-sm text-neutral-400">© 2026 Swishr Digital Ltd. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/about" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors">About</Link>
            <Link to="/security" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="footer-security-link">Security</Link>
            <Link to="/privacy" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/refunds" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="footer-refunds-link">Refund Policy</Link>
            <Link to="/terms" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
            <button onClick={openCookieSettings} className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="footer-cookie-settings">Cookie settings</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
